import { TabGroup } from '@headlessui/react';
import { HelpCircleIcon } from '@langchain/untitled-ui-icons';

import { isEqual } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';

import { Tooltip } from '@/design-system';
import { Button } from '@/design-system/components/Button';
import { Text } from '@/design-system/components/Text';
import OpenAILogo from '@/icons/OpenAILogo.svg?react';
import { DatasetTransformationSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';
import {
  jsonSchemaExtensions,
  useCodeMirrorTooltipFollowMouseManual,
} from '@/utils/use-codemirror-tooltip-follow-mouse';

import { DeleteConfirmationButton } from '../Delete';
import { EditInputFunctionParameters } from '../ExampleCrudPane/EditInputFunctions';
import { SchemaEditorVariant } from '../SchemaEditor/types';
import { Switch } from '../Switch/Switch';
import { TabLabel, TabList, TabPanel, TabPanels } from '../Tabs/Tabs';
import {
  CHAT_MODEL_SCHEMA_ONBOARDING_TEXT,
  DEFAULT_CHAT_INPUT_SCHEMA,
  DEFAULT_CHAT_OUTPUT_SCHEMA,
  DEFAULT_CHAT_TRANSFORMATIONS,
  DEFAULT_SCHEMA,
} from './constants';
import { useDatasetSchemaStore } from './hooks/useSchemaStore';
import { jsonSchema7MetaSchema } from './jsonSchema7';
import { getTransformationsToSet } from './utils';

const SchemaEditor = ({
  inputOutput,
  schema,
  setSchema,
  transformations,
  setTransformations,
}: {
  inputOutput?: 'input' | 'output';
  schema: Record<string, unknown> | null;
  setSchema: (schema: Record<string, unknown>) => void;
  transformations?: DatasetTransformationSchema[];
  setTransformations?: (transformations: DatasetTransformationSchema[]) => void;
}) => {
  // Avoid race condition around schema defaulting to an empty object
  // in some cases
  return schema && schema.type === 'object' ? (
    <EditInputFunctionParameters
      value={schema}
      onChange={(value) => setSchema(value as Record<string, unknown>)}
      readOnly={false}
      variant={SchemaEditorVariant.NESTED}
      showEditorSelector={true}
      codeEditorExtensions={{
        json: [jsonSchemaExtensions(jsonSchema7MetaSchema)],
      }}
      showBorder={true}
      transformations={transformations}
      setTransformations={setTransformations}
      pathPrefix={inputOutput === 'input' ? ['inputs'] : ['outputs']}
    />
  ) : (
    <div
      className={cn(
        'group flex flex-col rounded-md bg-background p-1 pl-3',
        'border border-secondary py-3'
      )}
    >
      <div className="text-xs font-normal text-tertiary">
        No schema defined.
      </div>
    </div>
  );
};

type DatasetSchemaEditorProps = {
  inputSchema: Record<string, unknown> | null;
  setInputSchema: React.Dispatch<
    React.SetStateAction<Record<string, unknown> | null>
  >;
  outputSchema: Record<string, unknown> | null;
  setOutputSchema: React.Dispatch<
    React.SetStateAction<Record<string, unknown> | null>
  >;
  template?: 'chat' | 'custom';
  showToggle: boolean;
  showSection: boolean;
};

export const DatasetSchemaEditor = ({
  inputSchema,
  setInputSchema,
  outputSchema,
  setOutputSchema,

  showSection,
}: DatasetSchemaEditorProps) => {
  const inputSchemaInputRef = useRef<HTMLInputElement>(null);
  const outputSchemaInputRef = useRef<HTMLInputElement>(null);

  // For preserving state if user clicks remove schema validation
  const [tmpInputSchema, setTmpInputSchema] = useState(inputSchema);
  const [tmpOutputSchema, setTmpOutputSchema] = useState(outputSchema);

  const [enableInputSchema, setEnableInputSchema] = useState(!!inputSchema);
  const [enableOutputSchema, setEnableOutputSchema] = useState(!!outputSchema);

  useCodeMirrorTooltipFollowMouseManual(
    inputSchemaInputRef,
    outputSchemaInputRef,
    showSection,
    showSection
  );

  useEffect(() => {
    if (!enableInputSchema) {
      if (!inputSchema && !tmpInputSchema) {
        setInputSchema(DEFAULT_SCHEMA('input'));
      } else if (tmpInputSchema !== inputSchema) {
        setInputSchema(tmpInputSchema);
      }
      setEnableInputSchema(true);
    }

    if (!enableOutputSchema) {
      if (!outputSchema && !tmpOutputSchema) {
        setOutputSchema(DEFAULT_SCHEMA('output'));
      } else if (tmpOutputSchema !== outputSchema) {
        setOutputSchema(tmpOutputSchema);
      }
      setEnableOutputSchema(true);
    }
  }, []);

  const datasetInputSchemaSection = (
    <div ref={inputSchemaInputRef}>
      <Switch
        checked={enableInputSchema}
        onChange={(checked) => {
          if (checked) {
            if (!inputSchema && !tmpInputSchema) {
              setInputSchema(DEFAULT_SCHEMA('input'));
            } else if (tmpInputSchema !== inputSchema) {
              setInputSchema(tmpInputSchema);
            }
          } else {
            setTmpInputSchema(inputSchema);
            setInputSchema(null);
          }
          setEnableInputSchema(checked);
        }}
        label="Define input schema"
        className="mb-2"
      ></Switch>

      {enableInputSchema && (
        <SchemaEditor schema={inputSchema} setSchema={setInputSchema} />
      )}
    </div>
  );

  const datasetOutputSchemaSection = (
    <div ref={outputSchemaInputRef}>
      <Switch
        label="Define output schema"
        checked={enableOutputSchema}
        onChange={(checked) => {
          if (checked) {
            if (!outputSchema && !tmpOutputSchema) {
              setOutputSchema(DEFAULT_SCHEMA('output'));
            } else if (tmpOutputSchema !== outputSchema) {
              setOutputSchema(tmpOutputSchema);
            }
          } else {
            setTmpOutputSchema(outputSchema);
            setOutputSchema(null);
          }
          setEnableOutputSchema(checked);
        }}
        className="mb-2"
      ></Switch>

      {enableOutputSchema && (
        <SchemaEditor schema={outputSchema} setSchema={setOutputSchema} />
      )}
    </div>
  );
  return (
    <div className="flex flex-col gap-4">
      {datasetInputSchemaSection}
      {datasetOutputSchemaSection}
    </div>
  );
};

export const ChatModelSchemaOnboarding = (props: {
  onApply: () => void;
  hasApplied: boolean;
}) => {
  return (
    !props.hasApplied && (
      <div
        className={cn(
          'flex items-start gap-4 rounded-lg border border-secondary p-4'
        )}
      >
        <div className="rounded-lg border border-primary p-2.5">
          <OpenAILogo className="size-5" />
        </div>
        <div className="flex-1">
          <div className="text-lg font-semibold">Apply chat model schema</div>
          <p className="text-sm text-secondary">
            <Text>{CHAT_MODEL_SCHEMA_ONBOARDING_TEXT}</Text>
            <div className="mt-2 flex items-center gap-3">
              <a
                href="https://docs.langchain.com/langsmith/dataset-transformations#chat-model-prebuilt-schema"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="underlined">Learn more</Button>
              </a>
              <Button
                variant="underlined"
                onClick={props.onApply}
                data-testid="apply-chat-model-schema-button"
              >
                Apply
              </Button>
            </div>
          </p>
        </div>
      </div>
    )
  );
};

export const InputOutputCommonHeader = (props: {
  hasApplied: boolean;
  schema: Record<string, unknown> | null;
  onApply: () => void;
  inputOutput: 'input' | 'output';
}) => {
  const matchesChatTemplate = isEqual(
    props.schema,
    props.inputOutput === 'input'
      ? DEFAULT_CHAT_INPUT_SCHEMA()
      : DEFAULT_CHAT_OUTPUT_SCHEMA()
  );

  return (
    <>
      {props.hasApplied && (
        <div
          className={cn(
            'flex items-center gap-3 self-start rounded-lg bg-tertiary px-3.5 py-1'
          )}
        >
          <Text>LLM chat model schema</Text>
          <Tooltip
            title={
              <div className="flex flex-col gap-2">
                <Text>{CHAT_MODEL_SCHEMA_ONBOARDING_TEXT}</Text>{' '}
                <a
                  href="https://docs.langchain.com/langsmith/dataset-transformations#chat-model-prebuilt-schema"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mr-auto"
                >
                  <Button variant="underlined">Learn more</Button>
                </a>
              </div>
            }
          >
            <div className="flex items-center gap-1">
              <HelpCircleIcon className="size-4" />
            </div>
          </Tooltip>
          <DeleteConfirmationButton
            title="Apply chat model schema"
            description="This will replace the current schema with the default chat model schema."
            onDelete={props.onApply}
            disabled={matchesChatTemplate}
            deleteButtonText="Replace"
            isDeleting={false}
            trigger={
              <button
                disabled={matchesChatTemplate}
                type="button"
                className="rounded-md border border-ls-black bg-background px-2 py-1 text-xs text-primary disabled:opacity-50"
              >
                {matchesChatTemplate ? 'Applied' : 'Apply'}
              </button>
            }
          />
        </div>
      )}
    </>
  );
};

export const AlwaysEnabledDatasetSchemaEditor = (props: {
  hideChatModelSchemaOnboarding?: boolean;
}) => {
  const inputSchemaInputRef = useRef<HTMLInputElement>(null);
  const outputSchemaInputRef = useRef<HTMLInputElement>(null);

  const inputSchema = useDatasetSchemaStore((state) => state.inputSchema);
  const setInputSchema = useDatasetSchemaStore((state) => state.setInputSchema);
  const outputSchema = useDatasetSchemaStore((state) => state.outputSchema);
  const setOutputSchema = useDatasetSchemaStore(
    (state) => state.setOutputSchema
  );
  const transformations = useDatasetSchemaStore(
    (state) => state.transformations
  );
  const setTransformations = useDatasetSchemaStore(
    (state) => state.setTransformations
  );
  const matchesChatTemplate =
    isEqual(inputSchema, DEFAULT_CHAT_INPUT_SCHEMA()) &&
    isEqual(outputSchema, DEFAULT_CHAT_OUTPUT_SCHEMA());

  const [hasAppliedChatModelSchema, setHasAppliedChatModelSchema] =
    useState(matchesChatTemplate);

  useCodeMirrorTooltipFollowMouseManual(
    inputSchemaInputRef,
    outputSchemaInputRef,
    true,
    true
  );

  const datasetInputSchemaSection = (
    <div ref={inputSchemaInputRef} className="flex flex-col gap-4">
      <InputOutputCommonHeader
        hasApplied={hasAppliedChatModelSchema}
        schema={inputSchema}
        onApply={() => {
          setInputSchema(DEFAULT_CHAT_INPUT_SCHEMA());
          setTransformations(
            getTransformationsToSet(transformations, 'inputs')
          );
        }}
        inputOutput="input"
      />
      <SchemaEditor
        schema={inputSchema}
        setSchema={setInputSchema}
        transformations={transformations}
        setTransformations={setTransformations}
        inputOutput="input"
      />
      <div className="flex justify-end">
        <Button
          variant="outlined"
          color={inputSchema ? 'error' : 'primary'}
          size="sm"
          data-testid="add-clear-input-schema"
          onClick={() => {
            if (inputSchema) {
              setTransformations(
                transformations?.filter((t) => t.path[0] !== 'inputs')
              );
              setInputSchema(null);
            } else {
              setInputSchema(DEFAULT_SCHEMA('input'));
            }
          }}
        >
          {!inputSchema ? 'Add input schema' : 'Clear input schema'}
        </Button>
      </div>
    </div>
  );

  const datasetOutputSchemaSection = (
    <div ref={outputSchemaInputRef} className="flex flex-col gap-4">
      <InputOutputCommonHeader
        hasApplied={hasAppliedChatModelSchema}
        schema={outputSchema}
        onApply={() => {
          setOutputSchema(DEFAULT_CHAT_OUTPUT_SCHEMA());
          setTransformations(
            getTransformationsToSet(transformations, 'outputs')
          );
        }}
        inputOutput="output"
      />
      <SchemaEditor
        key={outputSchema?.toString()}
        schema={outputSchema}
        setSchema={setOutputSchema}
        transformations={transformations}
        setTransformations={setTransformations}
        inputOutput="output"
      />
      <div className="flex justify-end">
        <Button
          variant="outlined"
          color={outputSchema ? 'error' : 'primary'}
          size="sm"
          data-testid="add-clear-output-schema"
          onClick={() => {
            if (outputSchema) {
              setTransformations(
                transformations?.filter((t) => t.path[0] !== 'outputs')
              );
              setOutputSchema(null);
            } else setOutputSchema(DEFAULT_SCHEMA('output'));
          }}
        >
          {!outputSchema ? 'Add output schema' : 'Clear output schema'}
        </Button>
      </div>
    </div>
  );
  return (
    <div className="flex flex-col gap-4">
      {!props.hideChatModelSchemaOnboarding && (
        <ChatModelSchemaOnboarding
          onApply={() => {
            setInputSchema(DEFAULT_CHAT_INPUT_SCHEMA());
            setOutputSchema(DEFAULT_CHAT_OUTPUT_SCHEMA());
            setTransformations(DEFAULT_CHAT_TRANSFORMATIONS);
            setHasAppliedChatModelSchema(true);
          }}
          hasApplied={hasAppliedChatModelSchema}
        />
      )}
      <TabGroup defaultIndex={0}>
        <TabList className="mb-4">
          <TabLabel>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <div className="flex items-center gap-2">Input schema</div>
            </div>
          </TabLabel>
          <TabLabel dataTestId="output-schema-tab">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <div className="flex items-center gap-2">Output schema</div>
            </div>
          </TabLabel>
        </TabList>

        <TabPanels>
          <TabPanel>{datasetInputSchemaSection}</TabPanel>
          <TabPanel>{datasetOutputSchemaSection}</TabPanel>
        </TabPanels>
      </TabGroup>
    </div>
  );
};
